/**
 * view.js — CRM Testimonial Slider
 * Loaded as a viewScriptModule (ES module, deferred).
 * One instance per block on the page.
 */

class TestimonialSlider {
	/**
	 * @param {HTMLElement} root  The .crm-testimonial-slider wrapper.
	 */
	constructor( root ) {
		this.root      = root;
		this.slides    = Array.from( root.querySelectorAll( '.crm-ts-slide' ) );
		this.dots      = Array.from( root.querySelectorAll( '.crm-ts-dot' ) );
		this.prevBtn   = root.querySelector( '.crm-ts-prev' );
		this.nextBtn   = root.querySelector( '.crm-ts-next' );

		this.total     = this.slides.length;
		this.current   = 0;
		this.timer     = null;

		this.autoPlay  = root.dataset.autoPlay === 'true';
		this.interval  = parseInt( root.dataset.interval, 10 ) || 5000;

		if ( this.total < 2 ) return; // nothing to slide

		this._bindEvents();
		if ( this.autoPlay ) this._startTimer();
	}

	// ── Public ──────────────────────────────────────────────────────────────

	goTo( index ) {
		const next = ( index + this.total ) % this.total;

		this.slides[ this.current ].classList.remove( 'is-active' );
		this.slides[ this.current ].setAttribute( 'aria-hidden', 'true' );
		this.dots[ this.current ]?.classList.remove( 'is-active' );
		this.dots[ this.current ]?.setAttribute( 'aria-selected', 'false' );

		this.current = next;

		this.slides[ this.current ].classList.add( 'is-active' );
		this.slides[ this.current ].setAttribute( 'aria-hidden', 'false' );
		this.dots[ this.current ]?.classList.add( 'is-active' );
		this.dots[ this.current ]?.setAttribute( 'aria-selected', 'true' );
	}

	// ── Private ─────────────────────────────────────────────────────────────

	_bindEvents() {
		this.prevBtn?.addEventListener( 'click', () => {
			this._resetTimer();
			this.goTo( this.current - 1 );
		} );

		this.nextBtn?.addEventListener( 'click', () => {
			this._resetTimer();
			this.goTo( this.current + 1 );
		} );

		this.dots.forEach( ( dot ) => {
			dot.addEventListener( 'click', ( e ) => {
				const index = parseInt( e.currentTarget.dataset.index, 10 );
				this._resetTimer();
				this.goTo( index );
			} );
		} );

		// Pause on hover / focus
		this.root.addEventListener( 'mouseenter', () => this._stopTimer() );
		this.root.addEventListener( 'mouseleave', () => {
			if ( this.autoPlay ) this._startTimer();
		} );
		this.root.addEventListener( 'focusin', () => this._stopTimer() );
		this.root.addEventListener( 'focusout', ( e ) => {
			if ( ! this.root.contains( e.relatedTarget ) && this.autoPlay ) {
				this._startTimer();
			}
		} );

		// Keyboard: left/right arrows when focused inside slider
		this.root.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'ArrowLeft' ) {
				this._resetTimer();
				this.goTo( this.current - 1 );
			} else if ( e.key === 'ArrowRight' ) {
				this._resetTimer();
				this.goTo( this.current + 1 );
			}
		} );
	}

	_startTimer() {
		this._stopTimer();
		this.timer = setInterval( () => this.goTo( this.current + 1 ), this.interval );
	}

	_stopTimer() {
		if ( this.timer ) {
			clearInterval( this.timer );
			this.timer = null;
		}
	}

	_resetTimer() {
		if ( this.autoPlay ) {
			this._stopTimer();
			this._startTimer();
		}
	}
}

// Boot all sliders on the page
document
	.querySelectorAll( '.crm-testimonial-slider' )
	.forEach( ( el ) => new TestimonialSlider( el ) );